<template>
  <LcExpandable
    ref="expandable"
    class="editor-info-expandable bg-secondary-secondary rounded-m"
    :header-class="['editor-info-expandable-header', `editor-info-expandable-header-${variant}`]"
    header-tag="h2"
    content-class="editor-info-expandable-content"
    animated
    @expanded="scrollExpandedIntoView(expandableRef)"
  >
    <template #header>
      <span class="editor-info-expandable-title">{{ title }}</span>
    </template>

    <template v-if="!isError">
      <component :is="component" v-bind="{ lotteryId }" @error="isError = true" />

      <div class="flex justify-center pt-400 pb-100">
        <LcButton
          variant="filled"
          @click="$emit('update:model-value', false)"
          icon="chevron-close"
          size="small"
        >
          {{ $t('info.expandable.cta.close') }}
        </LcButton>
      </div>
    </template>

    <IllustratedSection v-else class="mx-300" headline-tag="h3" illustration="loading-error">
      <template #headline>{{ $t('info.expandable.error.headline') }}</template>

      <template #subtitle>
        <i18n-t
          class="text-center"
          keypath="info.expandable.error.description"
          scope="global"
          tag="p"
        >
          <template #contactSupport>
            <a class="text-link" :href="$localePath('/service?chat=true')">
              {{ $t('common.sentences.contactsupport') }}
            </a>
          </template>
        </i18n-t>
      </template>
    </IllustratedSection>
  </LcExpandable>
</template>

<script lang="ts" setup>
import type { LcExpandable } from '@lottocom/frontend-components'
import type { ComponentExposed } from 'vue-component-type-helpers'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { LazyEditorRulesContent, LazyEditorFaqContent } from '#components'

type Props = {
  lotteryId: LotteryId
  variant: 'faqs' | 'rules'
}

const { variant } = defineProps<Props>()

const { t } = useI18n()
const { scrollExpandedIntoView } = useExpandableScroll()
const expandableRef = useTemplateRef<ComponentExposed<typeof LcExpandable>>('expandable')

const isError = ref(false)

const component = computed(() => {
  switch (variant) {
    case 'rules':
      return LazyEditorRulesContent
    case 'faqs':
      return LazyEditorFaqContent
  }
})

const title = computed(() => {
  switch (variant) {
    case 'rules':
      return t('editor.info.rules.title', 2)
    case 'faqs':
      return t('editor.info.faqs.title')
  }
})
</script>

<style lang="scss" scoped>
$x-spacing: token('size/space/gap/200');

.editor-info-expandable {
  overflow: hidden;
  box-shadow: token('shadow/shadow/card/large');
}

:deep(.editor-info-expandable-header) {
  position: relative;
  isolation: isolate;
  height: 3rem;

  .editor-info-expandable-title {
    padding-left: $x-spacing;
  }

  > i {
    padding: token('size/space/gap/none') $x-spacing;
  }

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    z-index: -1;
    pointer-events: none;
  }
}

:deep(.editor-info-expandable-content) {
  padding: token('size/space/gap/050') $x-spacing token('size/space/gap/150');
}

:deep(.editor-info-expandable-header-rules)::after {
  content: url('../assets/expandable-illustration-rules.svg');
}

:deep(.editor-info-expandable-header-faqs)::after {
  content: url('../assets/expandable-illustration-faqs.svg');
}
</style>
